// Operator-curated color palette for `select` custom-field choices.
// Stored on the choice as `{ value, label, color: "amber" }`. Optional —
// missing/unknown colors render as the neutral fallback. Adding a new
// color: append here AND to CustomFieldDefinition::CHOICE_COLORS in Ruby
// (the model validates against that list on save).
//
// Tailwind JIT requires literal class strings, so each entry hardcodes
// its triplet rather than building names at runtime.

export const CHOICE_COLORS = [
  "slate",
  "stone",
  "red",
  "orange",
  "amber",
  "yellow",
  "lime",
  "green",
  "emerald",
  "teal",
  "cyan",
  "blue",
  "indigo",
  "purple",
  "fuchsia",
  "pink",
] as const

export type ChoiceColor = (typeof CHOICE_COLORS)[number]

export type ChoiceColorClasses = {
  bg: string
  border: string
  text: string
  // Bold solid swatch for the picker UI (no transparency).
  swatch: string
}

// Each row is a literal string — Tailwind JIT only picks classes up
// when it sees the full token, so we can't build them at runtime.
// `red` / `amber` / `green` use the project's design tokens
// (`brand-red`, `status-warning`, `status-success`) so they match
// the rest of the portal; the other hues fall back to the standard
// Tailwind palette since we don't have project tokens for them.
const CLASSES: Record<ChoiceColor, ChoiceColorClasses> = {
  slate:   { bg: "bg-slate-500/20",   border: "border-slate-400/40",   text: "text-slate-200",   swatch: "bg-slate-500" },
  stone:   { bg: "bg-stone-500/20",   border: "border-stone-400/40",   text: "text-stone-200",   swatch: "bg-stone-500" },
  red:     { bg: "bg-brand-red/15",       border: "border-brand-red/40",      text: "text-brand-red",   swatch: "bg-brand-red" },
  orange:  { bg: "bg-orange-500/20",  border: "border-orange-400/40",  text: "text-orange-200",  swatch: "bg-orange-500" },
  amber:   { bg: "bg-status-warning/15",  border: "border-status-warning/40", text: "text-status-warning", swatch: "bg-status-warning" },
  yellow:  { bg: "bg-yellow-500/20",  border: "border-yellow-400/40",  text: "text-yellow-200",  swatch: "bg-yellow-500" },
  lime:    { bg: "bg-lime-500/20",    border: "border-lime-400/40",    text: "text-lime-200",    swatch: "bg-lime-500" },
  green:   { bg: "bg-status-success/15",  border: "border-status-success/40", text: "text-status-success", swatch: "bg-status-success" },
  emerald: { bg: "bg-emerald-500/20", border: "border-emerald-400/40", text: "text-emerald-200", swatch: "bg-emerald-500" },
  teal:    { bg: "bg-teal-500/20",    border: "border-teal-400/40",    text: "text-teal-200",    swatch: "bg-teal-500" },
  cyan:    { bg: "bg-cyan-500/20",    border: "border-cyan-400/40",    text: "text-cyan-200",    swatch: "bg-cyan-500" },
  blue:    { bg: "bg-blue-500/20",    border: "border-blue-400/40",    text: "text-blue-200",    swatch: "bg-blue-500" },
  indigo:  { bg: "bg-indigo-500/20",  border: "border-indigo-400/40",  text: "text-indigo-200",  swatch: "bg-indigo-500" },
  purple:  { bg: "bg-purple-500/20",  border: "border-purple-400/40",  text: "text-purple-200",  swatch: "bg-purple-500" },
  fuchsia: { bg: "bg-fuchsia-500/20", border: "border-fuchsia-400/40", text: "text-fuchsia-200", swatch: "bg-fuchsia-500" },
  pink:    { bg: "bg-pink-500/20",    border: "border-pink-400/40",    text: "text-pink-200",    swatch: "bg-pink-500" },
}

export function isChoiceColor(v: unknown): v is ChoiceColor {
  return typeof v === "string" && (CHOICE_COLORS as readonly string[]).includes(v)
}

export function choiceColorClasses(color: string | null | undefined): ChoiceColorClasses | null {
  if (!isChoiceColor(color)) return null
  return CLASSES[color]
}
